import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, Music2, X, Image as ImageIcon, GripVertical, CheckCircle2, Loader2, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SendCDPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=info, 2=cover, 3=tracks, 4=review
  const [form, setForm] = useState({
    artist_name: "", cd_title: "", email: "", whatsapp: "", instagram: "", description: ""
  });
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [tracks, setTracks] = useState([]); // {file, title}
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const coverInputRef = useRef(null);
  const tracksInputRef = useRef(null);

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onCoverChange = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("A capa deve ser uma imagem");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Capa muito grande (máx 10MB)");
      return;
    }
    setCover(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const onTracksChange = (files) => {
    const arr = Array.from(files || []);
    const valid = arr.filter(f => f.type.startsWith("audio/") || f.name.toLowerCase().endsWith(".mp3"));
    if (valid.length === 0) {
      toast.error("Selecione arquivos de áudio (MP3)");
      return;
    }
    if (tracks.length + valid.length > 30) {
      toast.error("Máximo 30 faixas");
      return;
    }
    const oversized = valid.find(f => f.size > 50 * 1024 * 1024);
    if (oversized) {
      toast.error(`Faixa "${oversized.name}" maior que 50MB`);
      return;
    }
    const newTracks = valid.map((f, i) => ({
      file: f,
      title: f.name.replace(/\.[^.]+$/, "").replace(/^\d+\s*[-_.]\s*/, "").trim()
    }));
    setTracks([...tracks, ...newTracks]);
  };

  const removeTrack = (idx) => setTracks(tracks.filter((_, i) => i !== idx));
  const updateTrackTitle = (idx, value) => {
    const next = [...tracks];
    next[idx] = { ...next[idx], title: value };
    setTracks(next);
  };

  const moveTrack = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= tracks.length) return;
    const next = [...tracks];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setTracks(next);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.artist_name || !form.cd_title || !form.email) {
        toast.error("Preencha nome do artista, título do CD e e-mail");
        return false;
      }
    }
    if (step === 2 && !cover) {
      toast.error("Adicione a capa do CD");
      return false;
    }
    if (step === 3 && tracks.length === 0) {
      toast.error("Adicione ao menos 1 faixa");
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep(step + 1);
  };

  const submit = async () => {
    setLoading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("cover", cover);
      tracks.forEach(t => fd.append("tracks", t.file));
      fd.append("track_titles", JSON.stringify(tracks.map(t => t.title)));

      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API}/api/cd-submissions`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        setLoading(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          setStep(5);
        } else {
          let msg = "Erro ao enviar";
          try {
            msg = JSON.parse(xhr.responseText).detail || msg;
          } catch (err) {
            console.warn("Could not parse error response", err);
          }
          toast.error(msg);
        }
      };
      xhr.onerror = () => {
        setLoading(false);
        toast.error("Erro de conexão");
      };
      xhr.send(fd);
    } catch (e) {
      setLoading(false);
      toast.error("Erro ao enviar");
    }
  };

  if (step === 5) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 pt-32 pb-20 text-center">
          <div className="w-24 h-24 bg-[#FF007F]/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <CheckCircle2 className="w-14 h-14 text-[#FF007F]" />
          </div>
          <h1 className="font-heading text-4xl font-black mb-4">CD Enviado!</h1>
          <p className="text-zinc-400 text-lg mb-8">
            Recebemos seu CD <span className="text-white font-semibold">"{form.cd_title}"</span>. Nossa equipe vai analisar e publicar na vitrine em até 48h.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/vitrine" className="px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all" data-testid="go-vitrine-btn">
              Ver Vitrine de CDs
            </Link>
            <Link to="/" className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full font-bold transition-all" data-testid="go-home-btn">
              Voltar ao Início
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      {/* Hero */}
      <div className="relative overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF007F]/20 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 py-10 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF007F]/10 border border-[#FF007F]/30 text-[#FF007F] text-xs font-bold mb-4">
            <Music2 size={14} /> VITRINE INDEPENDENTE
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] mb-3">
            Envie seu <span className="text-[#FF007F] italic">CD</span> de Arrocha.
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl">
            A vitrine 100% gratuita do arrochaplay. Mande sua capa, suas faixas e nossa equipe libera pro Brasil ouvir.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-4xl mx-auto px-4 mb-8">
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="flex-1">
              <div className={`h-1.5 rounded-full transition-all ${n <= step ? "bg-[#FF007F]" : "bg-white/10"}`} />
              <div className={`text-xs mt-2 font-semibold ${n <= step ? "text-[#FF007F]" : "text-zinc-600"}`}>
                {n === 1 && "Dados"}
                {n === 2 && "Capa"}
                {n === 3 && "Faixas"}
                {n === 4 && "Revisar"}
              </div>
            </div>
          ))}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 pb-20">
        {/* STEP 1: Info */}
        {step === 1 && (
          <div className="bg-[#0F0F0F] border border-white/10 rounded-3xl p-8 space-y-5">
            <h2 className="font-heading text-2xl font-bold mb-6">Conta pra gente quem você é</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Nome do Artista / Banda *</label>
                <input name="artist_name" value={form.artist_name} onChange={handleField}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#FF007F] focus:outline-none transition-colors"
                  placeholder="Ex: João do Arrocha" data-testid="input-artist-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Título do CD *</label>
                <input name="cd_title" value={form.cd_title} onChange={handleField}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#FF007F] focus:outline-none transition-colors"
                  placeholder="Ex: Promocional 2026" data-testid="input-cd-title" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">E-mail *</label>
                <input name="email" type="email" value={form.email} onChange={handleField}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#FF007F] focus:outline-none transition-colors"
                  placeholder="seu@email.com" data-testid="input-email" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-zinc-300 mb-2">WhatsApp</label>
                <input name="whatsapp" value={form.whatsapp} onChange={handleField}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#FF007F] focus:outline-none transition-colors"
                  placeholder="(00) 00000-0000" data-testid="input-whatsapp" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Instagram</label>
                <input name="instagram" value={form.instagram} onChange={handleField}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#FF007F] focus:outline-none transition-colors"
                  placeholder="@seuperfil" data-testid="input-instagram" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-zinc-300 mb-2">Descrição (opcional)</label>
                <textarea name="description" value={form.description} onChange={handleField} rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus:border-[#FF007F] focus:outline-none transition-colors resize-none"
                  placeholder="Conte um pouco sobre o CD, estilo, ocasião..." data-testid="input-description" />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Cover */}
        {step === 2 && (
          <div className="bg-[#0F0F0F] border border-white/10 rounded-3xl p-8">
            <h2 className="font-heading text-2xl font-bold mb-2">Capa do CD</h2>
            <p className="text-zinc-500 text-sm mb-6">JPG ou PNG. Quadrado de preferência. Máx 10MB.</p>

            <div
              onClick={() => coverInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onCoverChange(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-white/15 hover:border-[#FF007F]/60 rounded-2xl p-10 text-center cursor-pointer transition-all bg-black/30"
              data-testid="cover-dropzone"
            >
              {coverPreview ? (
                <div className="flex flex-col items-center gap-4">
                  <img src={coverPreview} alt="Capa" className="w-64 h-64 object-cover rounded-xl shadow-2xl" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCover(null); setCoverPreview(null); }}
                    className="text-sm text-zinc-400 hover:text-red-400 flex items-center gap-1"
                  >
                    <X size={14} /> Remover
                  </button>
                </div>
              ) : (
                <div>
                  <ImageIcon className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                  <p className="text-white font-semibold mb-1">Clique ou arraste a capa aqui</p>
                  <p className="text-zinc-500 text-sm">JPG / PNG · até 10MB</p>
                </div>
              )}
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => onCoverChange(e.target.files?.[0])} />
            </div>
          </div>
        )}

        {/* STEP 3: Tracks */}
        {step === 3 && (
          <div className="bg-[#0F0F0F] border border-white/10 rounded-3xl p-8">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <h2 className="font-heading text-2xl font-bold">Faixas do CD</h2>
              <span className="text-sm text-zinc-500">{tracks.length}/30 faixas</span>
            </div>
            <p className="text-zinc-500 text-sm mb-6">MP3, WAV, M4A. Máx 50MB por faixa. Você pode editar o nome.</p>

            <div
              onClick={() => tracksInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onTracksChange(e.dataTransfer.files); }}
              className="border-2 border-dashed border-white/15 hover:border-[#FF007F]/60 rounded-2xl p-8 text-center cursor-pointer transition-all bg-black/30 mb-5"
              data-testid="tracks-dropzone"
            >
              <Upload className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-white font-semibold mb-1">Clique ou arraste os arquivos de áudio</p>
              <p className="text-zinc-500 text-sm">Pode selecionar várias de uma vez</p>
              <input ref={tracksInputRef} type="file" accept="audio/*" multiple className="hidden"
                onChange={(e) => onTracksChange(e.target.files)} />
            </div>

            {tracks.length > 0 && (
              <div className="space-y-2">
                {tracks.map((t, idx) => (
                  <div key={t.id} className="flex items-center gap-3 bg-black/40 border border-white/5 rounded-xl p-3 hover:border-white/10 transition-colors" data-testid={`track-row-${idx}`}>
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => moveTrack(idx, -1)} disabled={idx === 0}
                        className="text-zinc-600 hover:text-white disabled:opacity-30 leading-none">▲</button>
                      <button onClick={() => moveTrack(idx, 1)} disabled={idx === tracks.length - 1}
                        className="text-zinc-600 hover:text-white disabled:opacity-30 leading-none">▼</button>
                    </div>
                    <span className="font-bold text-[#FF007F] tabular-nums w-8">{String(idx + 1).padStart(2, "0")}</span>
                    <input
                      value={t.title}
                      onChange={(e) => updateTrackTitle(idx, e.target.value)}
                      className="flex-1 bg-transparent border-b border-white/5 focus:border-[#FF007F] focus:outline-none py-1 text-sm"
                      data-testid={`track-title-${idx}`}
                    />
                    <span className="text-xs text-zinc-600 hidden sm:inline">{(t.file.size / 1024 / 1024).toFixed(1)} MB</span>
                    <button onClick={() => removeTrack(idx)} className="text-zinc-500 hover:text-red-400">
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 4: Review */}
        {step === 4 && (
          <div className="bg-[#0F0F0F] border border-white/10 rounded-3xl p-8">
            <h2 className="font-heading text-2xl font-bold mb-6">Revise e envie</h2>
            <div className="grid md:grid-cols-[200px_1fr] gap-6">
              <img src={coverPreview} alt="" className="w-full aspect-square object-cover rounded-2xl shadow-xl" />
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Artista</p>
                  <p className="text-xl font-bold">{form.artist_name}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">CD</p>
                  <p className="text-lg font-semibold text-[#FF007F]">{form.cd_title}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Contato</p>
                  <p className="text-sm text-zinc-300">{form.email}{form.whatsapp ? ` · ${form.whatsapp}` : ""}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Faixas ({tracks.length})</p>
                  <ol className="text-sm text-zinc-400 space-y-1 mt-1 list-decimal list-inside">
                    {tracks.map((t) => <li key={t.id}>{t.title}</li>)}
                  </ol>
                </div>
              </div>
            </div>

            {loading && (
              <div className="mt-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-zinc-400 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Enviando...</span>
                  <span className="text-[#FF007F] font-bold">{progress}%</span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF007F] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-8 gap-3">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} disabled={loading}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full font-semibold transition-all disabled:opacity-50"
              data-testid="back-btn">
              Voltar
            </button>
          ) : <div />}
          {step < 4 ? (
            <button onClick={next}
              className="px-8 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all"
              data-testid="next-btn">
              Continuar
            </button>
          ) : (
            <button onClick={submit} disabled={loading}
              className="px-8 py-3 bg-[#FF007F] hover:bg-[#FF3399] rounded-full font-bold transition-all flex items-center gap-2 disabled:opacity-50"
              data-testid="submit-cd-btn">
              {loading ? <Loader2 className="animate-spin" size={18}/> : <Send size={18}/>}
              {loading ? "Enviando..." : "Enviar CD"}
            </button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
